import apiClient from '@/lib/api';
import { PaginationMeta } from './agencies';

export interface AdminUserAgency {
  id: string; // agency publicId
  name: string;
  role: string;
}

export interface AdminUserRecord {
  id: string; // publicId
  firstName: string;
  lastName: string;
  email: string;
  globalRole: string | null;
  agencies: AdminUserAgency[];
  createdAtUtc: string;
}

export interface AdminUserDetailAgency {
  id: string;
  name: string;
  role: string;
  createdAtUtc: string;
}

export interface AdminUserDetailSubaccount {
  id: string;
  name: string;
  role: string;
  agencyId: string;
  agencyName: string;
  createdAtUtc: string;
}

export interface AdminUserDetailRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  emailVerifiedAtUtc: string | null;
  timezone: string | null;
  createdAtUtc: string;
  agencies: AdminUserDetailAgency[];
  subaccounts: AdminUserDetailSubaccount[];
}

export interface AdminUserEventRecord {
  id: string;
  eventType: string;
  metadata: any;
  createdAtUtc: string;
  agencyName: string | null;
}

export interface PaginatedUsersResponse {
  data: AdminUserRecord[];
  meta: PaginationMeta;
}

export interface PaginatedUserEventsResponse {
  data: AdminUserEventRecord[];
  meta: PaginationMeta;
}

export interface UserFilters {
  role?: string;
}

export interface AdminUserMagicLink {
  url: string;
  expiresAtUtc: string;
}

export const usersService = {
  /**
   * Fetch paginated list of users
   */
  getUsers: async (
    page: number = 1,
    limit: number = 20,
    search: string = '',
    sortBy: string = 'createdAtUtc',
    sortDir: string = 'desc',
    filters: UserFilters = {}
  ): Promise<PaginatedUsersResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search ? { search } : {}),
      sortBy,
      sortDir,
    });

    if (filters.role) params.set('role', filters.role);

    const response = await apiClient.get(`/admin/users?${params.toString()}`);
    return response.data as unknown as PaginatedUsersResponse;
  },

  /**
   * Fetch user details by ID
   */
  getUser: async (id: string): Promise<AdminUserDetailRecord> => {
    const response = await apiClient.get<{ data: AdminUserDetailRecord }>(`/admin/users/${id}`);
    return response.data.data;
  },

  /**
   * Fetch paginated platform events for a user
   */
  getUserEvents: async (
    id: string,
    params: { page?: number; limit?: number } = {}
  ): Promise<PaginatedUserEventsResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(params.page || 1));
    searchParams.set('limit', String(params.limit || 20));

    const response = await apiClient.get(`/admin/users/${id}/events?${searchParams.toString()}`);
    return response.data as unknown as PaginatedUserEventsResponse;
  },

  /**
   * Delete a user permanently
   */
  deleteUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/users/${id}`);
  },

  /**
   * Update a user's profile fields
   */
  updateUser: async (id: string, data: { firstName: string; lastName: string; email: string }): Promise<void> => {
    await apiClient.put(`/admin/users/${id}`, data);
  },

  /**
   * Trigger a password reset email for a user
   */
  resetUserPassword: async (id: string): Promise<void> => {
    await apiClient.post(`/admin/users/${id}/reset-password`);
  },

  /**
   * Generate a one-time login link for a user
   */
  generateMagicLink: async (id: string): Promise<AdminUserMagicLink> => {
    const response = await apiClient.post<{ data: AdminUserMagicLink }>(`/admin/users/${id}/magic-link`);
    return response.data.data;
  }
};
